/**
 ** module: SLTDirectoryPage.js
 ** usage: rendering logic for neighborhood directory page
 ** created: 12/1/2025
 ** owner: Rob Blanch
 **/
import React from "react";
import SLTDirectoryExport from "./SLTDirectoryExport";
import {directorySearchTerms, matchesDirectorySearch} from "./directorySearch";
import parseUtcDate from "./parseUtcDate";
import "./SLTDirectoryPage.css";
import SLTEditDirectoryEntry from "./SLTEditDirectoryEntry.js";
import MapOverlay from "./MapOverlay.jsx";

class SLTDirectoryPage extends React.Component {
  /**
   * constructor
   * usage: rendering logic for neighborhood directory page
   * props:
   *      lots - (mandatory) array of lot objects to display in directory
   *      (navItems) - (optional) callback to set nav items in parent component
   *      updateLot(lot) - (optional) callback to update lot data in neighbors
   *      getLotsAsGeoJSON(onComplete, onError) - (optional) callback to get lots as geoJSON for map overlay
   *      onGetUserPasskeys(onComplete, onError) - (optional) callback to retrieve the user's passkeys
   *      onCreatePasskey(onComplete, onError) - (optional) callback to handle creating a new passkey
   *      onDeletePasskey(credential_id, onComplete, onError) - (optional) callback to handle deleting a passkey
   */
  constructor(props) {
    super(props);
    this.searchInput = React.createRef();

    var msg = null;
    var error = null;
    var isMissingProps = false;

    if (!props.lots || !Array.isArray(props.lots)) {
      isMissingProps = true;
      msg = "SLTDirectoryPage: 'lots' prop is required and must be an array";
      error = new Error(msg);
    }

    if (!props.setNavItems || typeof props.setNavItems !== "function") {
      isMissingProps = true;
      msg = "SLTDirectoryPage: 'setNavItems' prop is required and must be a function";
      error = new Error(msg);
    }

    if (!props.getLotsAsGeoJSON || typeof props.getLotsAsGeoJSON !== "function") {
      isMissingProps = true;
      msg = "SLTDirectoryPage: 'getLotsAsGeoJSON' prop is required and must be a function";
      error = new Error(msg);
    }

    this.state = {
      error: error,
      message: msg,
      displayLots: props.lots || [],
      isMissingProps: isMissingProps,
      searchTerm: "",
      passkeyManage: false,
      directoryExport: false,
      passkeys: null,
      passkeysLoading: false,
      passkeysError: null,
      passkeysNotice: null,
      selectedPasskeyId: null,
      passkeyDeleting: false,
      editLot: null,
      showMap: false,
      showAddresses: false,
      geoJSONCache: null,
      selectedLotId: null,
    }
  }

  /**
   * returnFilteredLots
   *   return filtered lots based on search term
   */
  returnFilteredLots() {
    const terms = directorySearchTerms(this.state.searchTerm);
    return this.state.displayLots.filter(lot => matchesDirectorySearch([
      lot.lot_number, lot.lot_address && lot.lot_address.address_line1,
      ...(lot.owners || []).flatMap(owner => [
        owner.owner_name,
        ...(owner.phone_numbers || []).map(phone => phone.phone_number),
        ...(owner.email_addresses || []).map(email => email.email_address),
      ]),
    ], terms));
  }

  /**
   * setNavItems
   *    set nav items in parent component
   */
  setNavItems(navItems = null) {
    if (typeof this.props.setNavItems !== "function") return;
    if (this.state.passkeyManage || this.state.directoryExport) {
      this.props.setNavItems([
        {link: "#", label: "Back to Directory", icon: null, onclick: (event) => {
          event.preventDefault();
          this.returnToDirectory();
        }},
        {link: "/logout", label: "Sign Out", icon: null},
      ]);
      return;
    }
    if (this.state.editLot !== null && Array.isArray(navItems) && navItems.length > 0) {
      this.props.setNavItems(navItems);
      return;
    }
    const items = [];
    if (this.props.onCreatePasskey) {
      items.push({link: "#", label: "Passkey", icon: null, onclick: (event) => {
        event.preventDefault();
        this.setState({passkeyManage: true}, () => {
          this.setNavItems();
          this.loadUserPasskeys();
        });
      }});
    }
    items.push(
      {link: "#", label: "Print / Export", icon: null, onclick: (event) => {
        event.preventDefault();
        this.openDirectoryExport();
      }},
      {link: "#", label: this.state.showMap ? "Hide Map" : "Map", icon: null, onclick: (event) => {
        event.preventDefault();
        this.setState(state => ({showMap: !state.showMap}), () => this.setNavItems());
      }},
      {link: "#", label: this.state.showAddresses ? "Hide Mail" : "Show Mail", icon: null, onclick: (event) => {
        event.preventDefault();
        this.setState(state => ({showAddresses: !state.showAddresses}), () => this.setNavItems());
      }},
      {link: "/logout", label: "Sign Out", icon: null},
    );
    this.props.setNavItems(items);
  }

  openDirectoryExport() {
    this.setState({directoryExport: true, showMap: false}, () => this.setNavItems());
  }

  returnToDirectory() {
    this.setState({passkeyManage: false, directoryExport: false}, () => this.setNavItems());
  }

  /**
   * componentDidMount
   *    react js called routine once UI components are loaded
   */
  componentDidMount() {
    this.setNavItems();
  }

  /**
   * componentWillUnmount
   *    react js called routine once UI components are unloaded
   */
  componentWillUnmount() {
    // Clear nav items
    if (this.props.setNavItems && typeof this.props.setNavItems === "function") {
      this.props.setNavItems([]);
    }
  }

  /**
   * scrollToLot
   *   scroll to lot in directory
   */
  scrollToLot(lotId) {
    const element = document.getElementById(`lot_${lotId}`);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }

  /**
   * setSelectedLotId
   *    return selected lot id for map overlay
   */
  setSelectedLotId(lotId, stop_showing_map=false) {
    this.setState({ selectedLotId: lotId });
    this.scrollToLot(lotId);
    if (stop_showing_map === true) {
      this.setState({ showMap: false });
    }
  }

  /**
   * groupedRender
   *    render directory grouped by lot number
   */
  groupedRender() {
    var lots = (this.state.searchTerm !== undefined && this.state.searchTerm !== null && this.state.searchTerm !== "") ? this.returnFilteredLots() : this.state.displayLots;

    return (
      <div className="dir-root">
        <header className="dir-header">
          <h1>Neighborhood Directory</h1>
          <button type="button" className="dir-export-btn" onClick={() => this.openDirectoryExport()}>Print / Export</button>
          {/* Optional quick filter input */}
          <div className="dir-search">
            <input
              ref={this.searchInput}
              type="text"
              aria-label="Search directory"
              aria-describedby="dir-search-help"
              placeholder="Search by name, lot, or address…"
              value={this.state.searchTerm}
              onChange={(e) => this.setState({ searchTerm: e.target.value })}
            />
            {this.state.searchTerm && (
              <button type="button" className="dir-search-clear" aria-label="Clear search"
                onClick={() => this.setState({searchTerm: ""}, () => this.searchInput.current.focus())}>
                <span aria-hidden="true">×</span>
              </button>
            )}
          </div>
          <p id="dir-search-help" className="dir-search-help">Matches any term. Separate terms with commas or “and”.</p>
        </header>

        <main className="dir-content">
          <div className="dir-list">
            {lots.map((g) => (
              <article
                key={`${g.lot_number}_${g.neighborhood}`}
                className="dir-card dir-card--group"
              >
                {/* 👇 Group header: the shared address/lot */}
                <div key={g.lot_number} 
                     className={(parseInt(this.state.selectedLotId, 10) === parseInt(g.lot_number, 10)) ? "dir-group-header--selected" : "dir-group-header"}  
                     onClick={() => { this.setState({ selectedLotId: g.lot_number }); this.scrollToLot(g.lot_number); }}
                     id={"lot_" + g.lot_number}>
                  <div className="dir-group-header-text">
                    <div className="dir-group-address">{g.lot_address.address_line1 + " (Lot " + g.lot_number + ")"}</div>
                  </div>
                  {g.usercanmanage_lot > 0 && this.props.updateLot && (
                    <button
                      type="button"
                      className="dir-edit-btn"
                      onClick={() => { this.setState({ editLot: g }); }}
                      aria-label={"Edit information for address " + g.lot_address.address_line1 + " (Lot #" + g.lot_number + ")"}
                    >
                      ✏️
                    </button>
                  )}
                </div>

                {/* 👇 Individual people/contacts for this address */}
                <ul className="dir-members">
                  {g.owners.map((m, index) => (
                    ((m.show_public_directory) && (
                    <li key={index + "_" + g.lot_number} className="dir-member-row">
                      <div className="dir-member-name">{m.owner_name}</div>
                      <div className="dir-member-contact">
                        {m.phone_numbers && Array.isArray(m.phone_numbers) && m.phone_numbers.map((phone_number, index) => (
                          (phone_number.show_public_directory && (
                          <a key={index + "_" + g.lot_number + "_phone_"} href={`tel:${phone_number.phone_number.replace(/[^\d]/g, "")}`} className="dir-phone-link">
                            <span>{phone_number.phone_number}</span>
                            <span aria-hidden="true"> 📞</span>
                          </a>)) || null
                        ))}
                        {m.email_addresses && Array.isArray(m.email_addresses) && m.email_addresses.map((email_address, index) => (
                          (email_address.show_public_directory && (
                            <a key={index + "_" + g.lot_number + "_email_"} href={`mailto:${email_address.email_address}`} className="dir-email-link">
                              <span>{email_address.email_address}</span>
                              <span aria-hidden="true"> ✉️</span>
                            </a>)) || null
                        ))}
                        {this.state.showAddresses && m.mailing_addresses && Array.isArray(m.mailing_addresses) && m.mailing_addresses.map((mailing_address, index) => (
                          (mailing_address.show_public_directory && (
                          <div key={index + "_" + g.lot_number + "_mail_"} className="dir-mailing-address">
                            <address>
                              {mailing_address.address_line1}<br />
                              {mailing_address.address_line2 && (
                                <>
                                  {mailing_address.address_line2}<br />
                                </>
                              )}
                              {mailing_address.city}, {mailing_address.state_province_region} {mailing_address.postal_code}
                            </address>
                            <span className="dir-mailing-icon" aria-hidden="true"> 📮</span>
                            </div>)) || null
                        ))}
                      </div>
                    </li>)) || null
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <MapOverlay
            isOpen={this.state.showMap}
            onClose={() => {this.setState({ showMap: false })}}
            getLotsAsGeoJSON={this.getDirectoryLotsAsGeoJSON.bind(this)}
            selectedLotId={this.state.selectedLotId}
            onSelectLot={this.setSelectedLotId.bind(this)}   // optional; remove if you don’t want map->directory selection
            getLotOwnerDisplay={ (lotId) => {
              const lot = this.state.displayLots.find(l => parseInt(l.lot_number, 10) === parseInt(lotId, 10));
              if (lot) {
                const ownersDisplay = lot.owners
                  .filter(o => o.show_public_directory)
                  .map(o => o.owner_name)
                  .join(", ");
                return ownersDisplay || "(No owner display info)";
              }
              return "(No owner display info)";
            }}
          />
        </main>
      </div>
    );
  }

  /**
   * getDirectoryLotsAsGeoJSON
   *    get lots as geoJSON for map overlay. if cached return it, else request to cache
   */
  getDirectoryLotsAsGeoJSON() {
    if (this.state.geoJSONCache !== null) {
      return Promise.resolve(this.state.geoJSONCache);
    }

    return new Promise((resolve, reject) => {
      if (this.props.getLotsAsGeoJSON && typeof this.props.getLotsAsGeoJSON === "function") {
        this.props.getLotsAsGeoJSON(
          (geoJSON) => {
            this.setState({ geoJSONCache: geoJSON });
            resolve(geoJSON);
          },
          (error) => {
            this.setState({ error });
            reject(error);
          }
        );
      } else {
        const err = new Error("getLotsAsGeoJSON prop not provided");
        this.setState({ error: err });
        reject(err);
      }
    });
  };

  /**
   ** renderEditDirectoryEntry
   **    render edit directory entry page
   **/
  renderEditDirectoryEntry() {
    const setNavItems = this.setNavItems.bind(this);

    return (
      <SLTEditDirectoryEntry
        lot={this.state.editLot}
        onSave={(updatedEntry) => {
          this.props.updateLot(updatedEntry);
        }}
        onCancel={() => {
          this.setState({ editLot: null });
        }}
        setNavItems={setNavItems}
      />
    );
  }

  renderPasskeyManagement() {
    return (
      <div className="dir-root passkey-page">
        <header className="dir-header">
          <h1>Passkey Management</h1>
          <p>Manage your passkeys for authentication.</p>
        </header>

        <main className="dir-content passkey-content">
          <div className="passkey-management">
            {this.state.passkeysNotice && <p className="passkey-notice" role="status">{this.state.passkeysNotice}</p>}
            {this.renderPasskeyList()}
          </div>
          <div className="passkey-management-actions">
              <button
                type="button"
                className="passkey-create-btn"
                onClick={() => {
                  if (this.props.onCreatePasskey) {
                    this.props.onCreatePasskey(
                      (response) => {
                        console.log('Passkey created successfully:', response);
                        alert('Passkey created successfully!');
                        this.loadUserPasskeys();
                      },
                      (error) => {
                        console.error('Error creating passkey:', error);
                        alert('Error creating passkey: ' + error.message);
                      }
                    );
                  }
                }}
              >
                Create New Passkey
              </button>
              <button
                type="button"
                className="passkey-delete-btn"
                disabled={this.state.selectedPasskeyId === null || this.state.passkeyDeleting}
                onClick={this.deleteSelectedPasskey.bind(this)}
              >
                {this.state.passkeyDeleting ? "Deleting..." : "Delete Selected Passkey"}
              </button>
              <button
                type="button"
                className="passkey-back-btn"
                onClick={() => this.returnToDirectory()}
              >
                Back to Directory
              </button>
          </div>
        </main>
      </div>
    );
  }

  /**
   * loadUserPasskeys
   *   retrieve the passkeys belonging to the current user
   */
  loadUserPasskeys() {
    if (!this.props.onGetUserPasskeys || typeof this.props.onGetUserPasskeys !== "function") {
      this.setState({
        passkeys: [],
        passkeysLoading: false,
        passkeysError: new Error("Passkey retrieval is unavailable"),
      });
      return;
    }

    this.setState({ passkeysLoading: true, passkeysError: null, selectedPasskeyId: null });
    this.props.onGetUserPasskeys(
      (response) => {
        const passkeys = Array.isArray(response.passkeys) ? response.passkeys : [];
        this.setState({ passkeys, passkeysLoading: false, passkeysError: null });
      },
      (error) => {
        this.setState({
          passkeys: [],
          passkeysLoading: false,
          passkeysError: error instanceof Error ? error : new Error(error && error.message ? error.message : "Unable to load passkeys"),
        });
      }
    );
  }

  /**
   * deleteSelectedPasskey
   *   delete the selected passkey and refresh the user's passkey list
   */
  deleteSelectedPasskey() {
    const credential_id = this.state.selectedPasskeyId;

    if (credential_id === null || !this.props.onDeletePasskey || typeof this.props.onDeletePasskey !== "function") {
      return;
    }

    this.setState({ passkeyDeleting: true, passkeysError: null, passkeysNotice: null });
    this.props.onDeletePasskey(
      credential_id,
      () => {
        this.setState({
          passkeyDeleting: false, selectedPasskeyId: null,
          passkeysNotice: "Passkey removed from your account. To stop it appearing in sign-in suggestions, also remove the matching saved passkey from Apple Passwords or your password manager. The saved copy can no longer sign in to this account.",
        });
        this.loadUserPasskeys();
      },
      (error) => {
        this.setState({
          passkeyDeleting: false,
          passkeysError: error instanceof Error ? error : new Error(error && error.message ? error.message : "Unable to delete passkey"),
        });
      }
    );
  }

  /**
   * renderPasskeyList
   *   render loading, error, empty, or populated passkey state
   */
  renderPasskeyList() {
    if (this.state.passkeysLoading || this.state.passkeys === null) {
      return <p role="status">Loading passkeys...</p>;
    }

    if (this.state.passkeysError) {
      return <p className="passkey-error" role="alert">{this.state.passkeysError.message}</p>;
    }

    if (this.state.passkeys.length === 0) {
      return <p>You do not have any passkeys yet.</p>;
    }

    return (
      <ul className="passkey-list" aria-label="Your passkeys">
        {this.state.passkeys.map((passkey, index) => {
          const identifier = passkey.credential_id;
          const label = (passkey.user && passkey.user.username) || passkey.label || passkey.name || passkey.deviceType || `Passkey ${index + 1}`;
          const transports = Array.isArray(passkey.transports) ? passkey.transports.join(", ") : null;
          const friendlyName = typeof passkey.friendly_name === "string" ? passkey.friendly_name.trim() : "";
          const created = parseUtcDate(passkey.created);
          const hasCreated = created !== null && !Number.isNaN(created.getTime());
          const lastUsed = parseUtcDate(passkey.last_used);
          const hasLastUsed = lastUsed !== null && !Number.isNaN(lastUsed.getTime());
          const isSelected = identifier === this.state.selectedPasskeyId;

          return (
            <li key={identifier || index} className={`passkey-list-item${isSelected ? " passkey-list-item--selected" : ""}`}>
              <label className="passkey-list-selection">
                <input
                  type="radio"
                  name="selectedPasskey"
                  value={identifier || ""}
                  checked={isSelected}
                  disabled={!identifier || this.state.passkeyDeleting}
                  onChange={() => this.setState({ selectedPasskeyId: identifier, passkeysError: null })}
                />
                <span className="passkey-list-content">
                  <span className="passkey-list-label">{label}</span>
                  {friendlyName && (
                    <span className="passkey-list-detail">Friendly name: {friendlyName}</span>
                  )}
                  {hasCreated && (
                    <span className="passkey-list-detail">
                      Created: <time dateTime={created.toISOString()}>
                        {created.toLocaleString(undefined, {dateStyle: "medium", timeStyle: "short"})}
                      </time>
                    </span>
                  )}
                  {transports && <span className="passkey-list-detail">Available via {transports}</span>}
                  <span className="passkey-list-detail">
                    Last used: {hasLastUsed ? (
                      <time dateTime={lastUsed.toISOString()}>
                        {lastUsed.toLocaleString(undefined, {dateStyle: "medium", timeStyle: "short"})}
                      </time>
                    ) : (lastUsed === null ? "Never" : "Unavailable")}
                  </span>
                  {typeof passkey.backedUp === "boolean" && (
                    <span className="passkey-list-detail">{passkey.backedUp ? "Synced passkey" : "Device-bound passkey"}</span>
                  )}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    );
  }

  /**
   * render the reactjs directory page
   */
  render() {
    /**
     * handle improper props scenario
     */
    if (this.state.isMissingProps === true) {
      return (
        <div className="dir-root">
          <header className="dir-header">
            <h1>Neighborhood Directory</h1>
            <p>Error: {this.state.message}</p>
          </header>
        </div>
      );
    }

    /**
     * if manage a passkey, then render that UI
     */
    if (this.state.directoryExport) {
      return <SLTDirectoryExport lots={this.state.displayLots} search={this.state.searchTerm}
          onBack={() => this.returnToDirectory()} />;
    }

    if (this.state.passkeyManage === true) {
      return ( this.renderPasskeyManagement() );
    }

    /**
     * if editing a neighbor entry, then render that UI
     */
    if (this.state.editLot !== null) {
      return (this.renderEditDirectoryEntry());
    }

    /**
     * render full directory view
     */
    return (this.groupedRender());
  }
}

export default SLTDirectoryPage;
